import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ListSectionTemplate from "../../common/ListSectionTemplate";
import { adminChangeRole, adminInvite, adminRemoveMember, adminRenameOrg, getOrgDetails } from "../../../api/studioAPI";
import { colors, surfaces, buttons, form } from "../../../styles/index";

const row = { display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: `1px solid ${colors.border}`, flexWrap: "wrap" };
const muted = { color: colors.textMuted, fontSize: "0.85rem" };
const small = { minHeight: 36, padding: "6px 12px" };

// One customer org, for the platform admin (PlatformPanel): rename it, invite people, change roles, remove members, and see
// its plans. Every change lands in that org's audit trail under the admin's name.
export default function OrgDetails({ orgId, onBack }) {
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("creator");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = useCallback(() => {
    getOrgDetails(orgId).then((o) => { setOrg(o); setName(o.org_name); }).catch((e) => toast.error(e.message));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const out = await fn();
      if (done) toast.success(done);
      load();
      return out;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    const out = await act(() => adminInvite(orgId, email.trim(), role), `Invitation sent to ${email.trim()}`);
    if (out) { setEmail(""); setLastLink(out.link || ""); }
  };

  return (
    <ListSectionTemplate title={org ? org.org_name : "Organization"} loading={org === null}
      actions={<button type="button" style={buttons.secondary} onClick={onBack}>← All organizations</button>}>
      {org && (
        <>
          <form style={{ ...surfaces.card, textAlign: "left", marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}
            onSubmit={(e) => { e.preventDefault(); act(() => adminRenameOrg(orgId, name.trim()), "Renamed"); }}>
            <label style={{ flex: "2 1 240px" }}>
              <span style={form.label}>Name</span>
              <input style={form.input} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
            </label>
            <button type="submit" style={buttons.secondary} disabled={busy || !name.trim() || name.trim() === org.org_name}>Rename</button>
            <span style={{ ...muted, flexBasis: "100%" }}>{org.org_id} · created {org.created_at ? new Date(org.created_at).toLocaleDateString() : "-"}</span>
          </form>

          <h4 style={{ margin: "16px 0 8px" }}>Members</h4>
          <form onSubmit={invite} style={{ ...surfaces.card, textAlign: "left", marginBottom: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ flex: "2 1 220px" }}>
              <span style={form.label}>Invite by email</span>
              <input type="email" style={form.input} value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
            </label>
            <label style={{ flex: "1 1 120px" }}>
              <span style={form.label}>Role</span>
              <select style={form.input} value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
                <option value="creator">Creator</option>
                <option value="owner">Owner</option>
              </select>
            </label>
            <button type="submit" style={buttons.primary} disabled={busy || !email.trim()}>Send invitation</button>
            {lastLink && <p style={{ flexBasis: "100%", margin: 0, fontSize: "0.85rem", wordBreak: "break-all" }}>Send this link yourself: <code>{lastLink}</code></p>}
          </form>
          <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
            {org.members.map((m) => (
              <div key={m.username} style={row}>
                <div style={{ flex: "1 1 200px" }}>
                  <strong>{m.username}</strong>
                  <div style={muted}>{m.last_login ? `Last signed in ${new Date(m.last_login).toLocaleDateString()}` : "Hasn't signed in yet"}</div>
                </div>
                <select aria-label={`Role of ${m.username}`} value={m.role} disabled={busy}
                  onChange={(e) => act(() => adminChangeRole(orgId, m.username, e.target.value), "Role changed")}
                  style={{ ...form.input, width: "auto", ...small }}>
                  <option value="creator">Creator</option>
                  <option value="owner">Owner</option>
                </select>
                <button type="button" style={{ ...buttons.secondary, ...small }} disabled={busy}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(`Remove ${m.username} from ${org.org_name}? Their plans stay with the organization.`)) {
                      act(() => adminRemoveMember(orgId, m.username), "Removed");
                    }
                  }}>
                  Remove
                </button>
              </div>
            ))}
            {org.members.length === 0 && <p style={{ padding: 16, margin: 0, color: colors.textMuted }}>Nobody has joined yet.</p>}
          </div>

          <h4 style={{ margin: "16px 0 8px" }}>Plans</h4>
          <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
            {org.plans.map((p) => (
              <div key={p.id} style={row}>
                <span style={{ flex: "1 1 240px" }}>{p.goal || p.id}</span>
                <span style={muted}>{p.missions} mission(s)</span>
                <span style={muted}>{p.created_by || "-"} · {p.created_at ? new Date(p.created_at).toLocaleDateString() : "-"}</span>
              </div>
            ))}
            {org.plans.length === 0 && <p style={{ padding: 16, margin: 0, color: colors.textMuted }}>No plans yet.</p>}
          </div>
        </>
      )}
    </ListSectionTemplate>
  );
}
